/**
 * Cohesive ChatKit — small shared atoms: Favicon, Disclose, FileTypeMark,
 * AttachmentChip.
 *
 * Ported from cohesive-design-system/showcase/lib/chat-kit.jsx onto plain
 * Tailwind utilities (type-scale translation: DS text-sm→text-xs,
 * text-base→text-sm, text-md→text-md).
 */
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { type ChatTint, FAV_TINTS, TINT_BG } from "./tints";

/** Domain monogram — first letter on a stable identity tint. */
export function Favicon({
  domain,
  size = 18,
}: {
  domain: string;
  size?: 16 | 18;
}) {
  const clean = (domain || "?").replace(/^www\./, "");
  let h = 0;
  for (const ch of domain || "") {
    h = (h * 31 + ch.charCodeAt(0)) % FAV_TINTS.length;
  }
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-xs font-sans text-[9px] font-semibold text-paper",
        size === 16 ? "size-4" : "size-[18px]",
        TINT_BG[FAV_TINTS[h]],
      )}
    >
      {clean[0]?.toUpperCase() ?? "?"}
    </span>
  );
}

/** Show more / Show less affordance for clamped content. */
export function Disclose({
  open,
  onToggle,
  more = "Show more",
  less = "Show less",
  className,
}: {
  open: boolean;
  onToggle: () => void;
  more?: string;
  less?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "mt-1.5 inline-flex items-center gap-1 font-sans text-xs font-medium text-rust outline-none transition-colors duration-150 ease-out hover:text-[color-mix(in_oklab,var(--rust)_80%,var(--ink))] focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-2",
        className,
      )}
    >
      {open ? less : more}
      <ChevronDown
        className={cn(
          "size-3 transition-transform duration-150",
          open && "rotate-180",
        )}
      />
    </button>
  );
}

/** File-type square used inside attachment + file chips. */
export function FileTypeMark({
  ext,
  tint = "sage",
}: {
  ext: string;
  tint?: ChatTint;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-[22px] min-w-[22px] shrink-0 items-center justify-center rounded-xs px-1 font-mono text-[8px] font-semibold uppercase text-paper",
        TINT_BG[tint] ?? "bg-sage",
      )}
    >
      {ext}
    </span>
  );
}

export interface ChatAttachment {
  name: string;
  ext: string;
  tint?: ChatTint;
  /** Mono metadata token, e.g. a formatted file size ("1.2 MB"). */
  meta?: string;
}

/** Attachment chip: file-type mark + name (+ meta, + remove when removable). */
export function AttachmentChip({
  attachment,
  onRemove,
  className,
}: {
  attachment: ChatAttachment;
  onRemove?: () => void;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md bg-n-05 font-sans text-xs text-n-80 shadow-hairline",
        onRemove ? "py-1.5 pl-1.5 pr-1.5" : "px-1.5 py-1",
        className,
      )}
    >
      <FileTypeMark ext={attachment.ext} tint={attachment.tint} />
      <span className="max-w-[220px] truncate font-medium">
        {attachment.name}
      </span>
      {attachment.meta && (
        <span className="font-mono text-2xs text-n-50">{attachment.meta}</span>
      )}
      {onRemove && (
        <button
          type="button"
          title="Remove"
          onClick={onRemove}
          className="inline-flex size-[18px] items-center justify-center rounded-xs text-n-40 outline-none transition-colors hover:bg-n-10 hover:text-n-80 focus-visible:outline-2 focus-visible:outline-rust"
        >
          <svg
            viewBox="0 0 12 12"
            className="size-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            <path d="M9 3 3 9M3 3l6 6" />
          </svg>
        </button>
      )}
    </span>
  );
}
